import { useEffect } from 'react';

import { useSale } from '../../../../hooks/sale/useSale';

import { usePaymentMethod } from '../../../../hooks/usePaymentMethod';

import { useAllowedOptions } from '../../../../hooks/settings/useAllowedOptions';

import { useSystemOperationMode } from '../../../../hooks/settings/useSystemOperationMode';

import POSPaymentMethodButton from '../paymentMethods/POSPaymentMethodButton';

import POSPaymentItemSection from '../paymentMethods/POSPaymentItemSection';

const CASH_METHOD_CODE = '01';

const POSPaymentMethods = () => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const {
    form,

    addPayment,

    updatePayment,

    clearAllPayments,

    handlerFinalizeSale,

    handlerSendToCashier,

    isCashierProcessingMode,
  } = useSale();

  const { paymentMethodDropdown, fetchPaymentMethodDropdown } =
    usePaymentMethod();

  const {
    getCodes,

    fetchCodesByModuleAndTarget,
  } = useAllowedOptions();

  const { form: operationConfig, fetchActiveConfiguration } =
    useSystemOperationMode();

  // =====================================================
  // 🔹 Effects
  // =====================================================
  useEffect(() => {
    fetchPaymentMethodDropdown();

    fetchCodesByModuleAndTarget('SALES', 'PAYMENT_METHOD');

    fetchActiveConfiguration();
  }, []);

  // =====================================================
  // 🔹 States
  // =====================================================
  const payments = form?.payments || [];

  const totalToPay = form?.totals?.totalToPay ?? 0;

  const selectedMethodCode = payments[0]?.paymentMethodCode;

  // =====================================================
  // 🔹 Allowed Methods
  // =====================================================
  const allowedCodes = getCodes('SALES', 'PAYMENT_METHOD') || [];

  const EXCLUDED_PAYMENT_METHODS = ['13', '99'];

  const filteredPaymentMethods = (
    allowedCodes.length === 0
      ? paymentMethodDropdown
      : paymentMethodDropdown.filter((method) =>
          allowedCodes.includes(method.code)
        )
  ).filter((method) => !EXCLUDED_PAYMENT_METHODS.includes(method.code));

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handleSelectPayment = (methodCode: string) => {
    clearAllPayments();

    addPayment({
      paymentMethodCode: methodCode,

      amount: totalToPay,

      // Prellenado con el total a pagar: la mayoría paga el monto exacto,
      // y el cajero puede editarlo si el cliente entregó otra cantidad.
      cashReceived: methodCode === '01' ? totalToPay : undefined,

      changeAmount: 0,

      reference: '',
    });
  };

  // =====================================================
  // 🔹 Default: Billetes y monedas
  // =====================================================
  // La mayoria de cobros son en efectivo; asi el cajero solo escribe el
  // monto recibido en vez de tener que elegir la forma de pago primero.
  // Solo aplica si todavia no hay ningun pago agregado.
  useEffect(() => {
    if (payments.length > 0) return;
    if (!filteredPaymentMethods.some((m) => m.code === CASH_METHOD_CODE)) return;

    handleSelectPayment(CASH_METHOD_CODE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments.length, filteredPaymentMethods]);

  // =====================================================
  // 🔹 Enter en "Efectivo recibido" finaliza/envia a caja
  // =====================================================
  const isPendingCashierMode =
    operationConfig.saleOperationMode === 'PENDING_CASHIER';

  const shouldSendToCashier = isPendingCashierMode && !isCashierProcessingMode;

  const hasProducts = (form?.products || []).length > 0;

  const handleEnterSubmit = () => {
    if (!hasProducts) return;

    if (shouldSendToCashier) {
      handlerSendToCashier(form.id);
    } else {
      handlerFinalizeSale(form);
    }
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
      mt-6

      border-t
      border-stroke

      pt-5

      dark:border-strokedark
    "
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}

      <div className="mb-4">
        <h3
          className="
          text-sm
          font-semibold

          text-black

          dark:text-white
        "
        >
          Forma de Pago
        </h3>

        <p
          className="
          mt-1

          text-xs

          text-body
        "
        >
          Selecciona el método de pago.
        </p>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Payment Methods */}
      {/* ===================================================== */}

      <div
        className="
        grid
        grid-cols-2
        gap-2
      "
      >
        {filteredPaymentMethods.map((method) => (
          <POSPaymentMethodButton
            key={method.id}
            label={method.name}
            active={selectedMethodCode === method.code}
            onClick={() => handleSelectPayment(method.code)}
          />
        ))}
      </div>

      {/* ===================================================== */}
      {/* 🔹 Payment Detail */}
      {/* ===================================================== */}

      {payments.length === 1 && (
        <div className="mt-4">
          <POSPaymentItemSection
            payment={payments[0]}
            totalToPay={totalToPay}
            onChange={(updatedPayment) => updatePayment(0, updatedPayment)}
            onEnter={handleEnterSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default POSPaymentMethods;
